/*
 * TestHelden · Offene Tests ohne IQ-Wert (Mentale Rotation, Denkfallen-Test) · assets/th-cogtest-offen.js
 * <th-cogtest-offen data-instrument="mentale-rotation|denkfallen"> läuft gegen dieselbe Plattform-Engine wie der
 * IQ-Test (app.testhelden.com/api/mobile/iq-test/*), aber als eigene Komponente: Der IQ-Test (th-cogtest.js) bleibt
 * unberührt. Ergebnis = Rohwert + Einordnung laut Originaldaten, kein IQ. Kein Tracking, keine Drittanbieter.
 */
(() => {
  'use strict';
  if (window.customElements && customElements.get('th-cogtest-offen')) return;
  const VERSION = '2026-10-21.1';
  const NF0 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
  const NF1 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1, minimumFractionDigits: 1 });
  const SVGNS = 'http://www.w3.org/2000/svg';

  function h(tag, attrs, ...kids) {
    const e = document.createElement(tag);
    if (attrs) for (const [k, v] of Object.entries(attrs)) {
      if (v === null || v === undefined || v === false) continue;
      if (k === 'class') e.className = v;
      else if (k === 'text') e.textContent = v;
      else if (k.startsWith('on') && typeof v === 'function') e.addEventListener(k.slice(2), v);
      else e.setAttribute(k, v === true ? '' : String(v));
    }
    for (const kid of kids.flat()) if (kid !== null && kid !== undefined && kid !== false) e.append(kid.nodeType ? kid : document.createTextNode(String(kid)));
    return e;
  }
  function s(tag, attrs, ...kids) {
    const e = document.createElementNS(SVGNS, tag);
    if (attrs) for (const [k, v] of Object.entries(attrs)) if (v !== null && v !== undefined) e.setAttribute(k, String(v));
    for (const kid of kids.flat()) if (kid) e.append(kid.nodeType ? kid : document.createTextNode(String(kid)));
    return e;
  }
  const store = {
    get(k) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* privat */ } },
    del(k) { try { localStorage.removeItem(k); } catch (e) { /* egal */ } },
  };
  const geraet = () => { const w = window.innerWidth; const coarse = window.matchMedia && matchMedia('(pointer: coarse)').matches; return w < 600 ? 'mobil' : (w < 1024 && coarse) ? 'tablet' : 'desktop'; };
  const reduceMotion = () => window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  function scrollTop(el, sofort) {
    const y = el.getBoundingClientRect().top + window.scrollY - 88;
    if (Math.abs(window.scrollY - y) > 40) window.scrollTo({ top: Math.max(0, y), behavior: sofort || reduceMotion() ? 'auto' : 'smooth' });
  }
  // Element in den sichtbaren Bereich unter der festen Kopfzeile holen, nur wenn nötig (Abnahme 2, Opus N1: mit
  // scrollIntoView „nearest“ lag der Fokus bei 390 px unter der Kopfzeile)
  function inSicht(el) {
    const kopf = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const oben = Math.max(88, kopf + 16);
    const r = el.getBoundingClientRect();
    if (r.top >= oben && r.bottom <= window.innerHeight - 16) return;
    const platz = window.innerHeight - oben - 16;
    const versatz = r.height < platz ? oben + (platz - r.height) / 2 : oben;
    // sofort statt weich (die Seite hat scroll-behavior: smooth): Der Dialog lag davor, ein Gleiten ließe den Fokus
    // kurz außerhalb des Bildes (Abnahme 3, Opus: Zwischenwert nach 500 ms) und ruht in Hintergrund-Tabs ganz
    const wurzel = document.documentElement, war = wurzel.style.scrollBehavior;
    wurzel.style.scrollBehavior = 'auto';
    window.scrollTo(0, Math.max(0, window.scrollY + r.top - versatz));
    wurzel.style.scrollBehavior = war;
  }
  async function api(base, path, opts) {
    const r = await fetch(base + path, Object.assign({ headers: { 'Content-Type': 'application/json' }, credentials: 'omit' }, opts));
    let body = null;
    try { body = await r.json(); } catch (e) { body = null; }
    if (!r.ok) {
      const err = new Error((body && body.error) || 'Verbindung fehlgeschlagen (' + r.status + ')');
      err.status = r.status;
      throw err;
    }
    return body;
  }
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  // eigene, neutrale Kurztexte statt Produkt-Werbesätzen
  const SATZ = {
    'figurenreihen-raeumliches-denken': 'Räumliches Denken und Figurenreihen üben, mit Lösungen.',
    'matrizentest-online-testtrainer': 'Matrizen gezielt üben, mit Lösungsweg zu jeder Aufgabe.',
    'logik-trainer-logik-raetsel-mit-loesungen': 'Logikaufgaben und Denkrätsel üben, mit Lösungen.',
    'einstellungstest-vorbereitung': 'Typische Aufgaben aus Einstellungstests üben, mit Lernplan.',
  };

  class ThCogtestOffen extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.setAttribute('data-thct-version', VERSION);
      this.api = (this.dataset.api || 'https://app.testhelden.com').replace(/\/$/, '');
      this.instrument = this.dataset.instrument || 'mentale-rotation';
      this.kRun = 'thct:' + this.instrument + ':lauf';
      this.kHist = 'thct:' + this.instrument + ':verlauf';
      this.testlauf = /[?&]testlauf=1(&|$)/.test(location.search);
      try { this.produkte = JSON.parse((this.querySelector('[data-thct-produkte]') || {}).textContent || '[]'); } catch (e) { this.produkte = []; }
      this.stage = this.querySelector('[data-thct-buehne]');
      // Startseite ohne den vorab gefüllten Hinweis merken (snippets/th-cogtest-offen-hinweis.liquid; bindIntro() setzt ihn neu)
      const kopie = this.stage.cloneNode(true);
      const vorab = kopie.querySelector('[data-thct-hinweis]');
      if (vorab) { vorab.replaceChildren(); vorab.hidden = true; }
      this.introHtml = kopie.innerHTML;
      this.bindIntro();
    }
    neuStart() {
      this.guardOff();
      store.del(this.kRun);
      this.stage.innerHTML = this.introHtml;
      this.bindIntro();
      scrollTop(this);
      // Fokus auf den Startknopf statt auf BODY (QA-Runde 3, Opus N-6)
      const start = this.stage.querySelector('[data-thct-start]');
      if (start) start.focus({ preventScroll: true });
    }

    bindIntro() {
      const btn = this.stage.querySelector('[data-thct-start]');
      if (btn) { btn.disabled = false; btn.addEventListener('click', () => this.start()); }
      const slot = this.stage.querySelector('[data-thct-hinweis]');
      const run = store.get(this.kRun);
      // Freitext aus älteren Fassungen nicht im Browser liegen lassen (Opus CRT-3)
      if (run && run.answers && Object.values(run.answers).some((x) => x && typeof x.answer === 'string' && x.answer.length > 1)) {
        run.answers = {};
        run.idx = 0;
        if (run.phase === 'angaben') run.phase = 'test';
        store.set(this.kRun, run);
      }
      if (slot && run && run.session && Date.now() - run.startedAt < 2.5 * 3600e3) {
        slot.hidden = false;
        slot.replaceChildren(h('p', { text: 'Du hast den Test angefangen und noch nicht abgeschlossen.' }),
          h('div', { class: 'thct-zeile' },
            h('button', { type: 'button', class: 'th-btn th-btn--secondary', onclick: () => { this.stageHalten(); this.resume(run); } }, 'Fortsetzen'),
            h('button', { type: 'button', class: 'thct-link', onclick: () => { store.del(this.kRun); slot.hidden = true; } }, 'Verwerfen')));
      } else if (slot) {
        slot.hidden = true;
        slot.replaceChildren();
      }
    }

    clear() { if (this._t) { clearTimeout(this._t); this._t = null; } if (this._iv) { clearInterval(this._iv); this._iv = null; } document.onkeydown = null; }
    save() {
      // Freitext-Antworten nie im Browser speichern (Opus CRT-3): Bei einem Abbruch beginnen die Fragen neu.
      const frei = this.items && this.items.some((x) => x.type === 'freitext');
      store.set(this.kRun, frei ? Object.assign({}, this.run, { answers: {}, idx: 0, phase: this.run.phase === 'angaben' ? 'test' : this.run.phase }) : this.run);
    }
    // Layoutsprünge vermeiden (QA-Runde 2): Bühne hält ab dem Start mindestens ihre Höhe und die Fensterhöhe. Bei der
    // Abgabe (faktor 2) sofort an den Anfang der Bühne, damit nichts darunter im Bild ist, wenn das Ergebnis kommt
    // (QA-Runde 3, Opus M-5, wie im IQ-Test)
    stageHalten(faktor) {
      if (!this.stage) return;
      if (faktor) scrollTop(this, true);
      this.stage.style.minHeight = Math.ceil(Math.max(this.stage.getBoundingClientRect().height, (faktor || 1) * window.innerHeight)) + 'px';
    }
    // Verlassen-Schutz wie im IQ-Test (QA final, Fable N3): Die Zurück-Taste zeigt einen Dialog als festes Overlay,
    // beim Neuladen oder Schließen fragt der Browser nach. An ab der ersten Übung, aus bei den Angaben.
    guardOn() {
      if (this._guard) return;
      this._guard = true;
      try { history.pushState({ thct: 1 }, ''); } catch (e) { /* egal */ }
      this._onPop = () => {
        if (!this._guard) return;
        try { history.pushState({ thct: 1 }, ''); } catch (e) { /* egal */ }
        this.leaveDialog();
      };
      this._onUnload = (ev) => { if (this._guard) { ev.preventDefault(); ev.returnValue = ''; } };
      window.addEventListener('popstate', this._onPop);
      window.addEventListener('beforeunload', this._onUnload);
      try { this._scrollWar = history.scrollRestoration; history.scrollRestoration = 'manual'; } catch (e) { /* egal */ }
    }
    guardOff() {
      if (!this._guard) return;
      this._guard = false;
      window.removeEventListener('popstate', this._onPop);
      window.removeEventListener('beforeunload', this._onUnload);
      try { if (this._scrollWar) history.scrollRestoration = this._scrollWar; } catch (e) { /* egal */ }
      if (this._dialogAus) this._dialogAus();
      const offen = this.querySelector('.thct-verlassen');
      if (offen) offen.remove();
    }
    leaveDialog() {
      if (this.querySelector('.thct-verlassen')) return;
      const frei = this.items && this.items.some((x) => x.type === 'freitext');
      const vorher = this.contains(document.activeElement) ? document.activeElement : null;
      // Fokus und Tasten bleiben im Dialog, auch wenn dahinter neu gezeichnet wird (Abnahme 3, Fable N1: lief die
      // Bildzeit ab, zog das nächste Bild bzw. die Rückmeldung der Übung den Fokus aus dem Dialog, Escape wirkte nicht
      // mehr). Die Zeit läuft bewusst weiter, sonst ließe sich der Test anhalten. Was hinter dem Dialog den Fokus
      // bekommen wollte, bekommt ihn nach dem Schließen: die aktuelle Aufgabe.
      let gewuenscht = null;
      const amDokument = (ev) => {
        if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); schliessen(); return; }
        if (ev.key === 'Tab') { ev.preventDefault(); ev.stopPropagation(); (document.activeElement === weiter ? verlassen : weiter).focus(); return; }
        if (!box.contains(ev.target)) { ev.preventDefault(); ev.stopPropagation(); }
      };
      const fokusFalle = (ev) => {
        if (box.contains(ev.target)) return;
        if (this.stage.contains(ev.target)) gewuenscht = ev.target;
        weiter.focus({ preventScroll: true });
      };
      const aus = () => {
        document.removeEventListener('keydown', amDokument, true);
        document.removeEventListener('focusin', fokusFalle, true);
        this._dialogAus = null;
      };
      const schliessen = () => {
        aus();
        box.remove();
        const aktuell = (el) => el && el.isConnected && this.stage.contains(el);
        const ziel = aktuell(gewuenscht) ? gewuenscht : aktuell(vorher) ? vorher : this.stage.querySelector('.thco-feedback button') || this.stage.querySelector('.thct-frage, .thct-titel, input');
        if (ziel) {
          ziel.focus({ preventScroll: true });
          // Fokus sichtbar machen, ohne die Seite springen zu lassen (Opus N14), und nicht unter der Kopfzeile (Abnahme 2, Opus N1)
          inSicht(ziel);
        }
      };
      const weiter = h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: schliessen }, 'Weiter testen');
      const verlassen = h('button', { type: 'button', class: 'thct-link', onclick: () => { this.guardOff(); history.go(-2); } }, 'Test verlassen');
      const box = h('div', { class: 'thct-verlassen', role: 'alertdialog', 'aria-modal': 'true', 'aria-labelledby': 'thco-verlassen-t' },
        h('div', { class: 'thct-verlassen__box' },
          h('p', { id: 'thco-verlassen-t' }, h('b', { text: 'Test verlassen?' }), frei
            ? ' Deine eingetippten Antworten speichern wir nicht, sie wären dann weg.'
            : ' Deine Antworten bleiben auf diesem Gerät gespeichert, aber die Zeit für das aktuelle Bild läuft weiter.'),
          h('div', { class: 'thct-zeile' }, weiter, verlassen)));
      // Escape, Tab-Falle und die Sperre für F/J am Dokument (Erfassungsphase), solange der Dialog offen ist
      document.addEventListener('keydown', amDokument, true);
      document.addEventListener('focusin', fokusFalle, true);
      this._dialogAus = aus;
      this.append(box);
      weiter.focus({ preventScroll: true });
    }
    msg(text, busy) {
      this.clear();
      this.stage.replaceChildren(h('div', { class: 'thct-meldung', role: 'status', 'aria-live': 'polite' }, busy ? h('span', { class: 'thct-spinner', 'aria-hidden': 'true' }) : null, h('p', { text })));
    }
    fehler(text, retry, neu) {
      this.clear();
      this.stage.replaceChildren(h('div', { class: 'thct-meldung thct-meldung--fehler', role: 'alert' }, h('p', { text }),
        h('div', { class: 'thct-zeile' },
          neu ? h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: neu }, 'Neu starten') : null,
          retry ? h('button', { type: 'button', class: 'th-btn th-btn--secondary', onclick: retry }, 'Nochmal versuchen') : null)));
    }

    async start() {
      this.stageHalten();
      this.msg('Aufgaben werden geladen …', true);
      try {
        const session = await api(this.api, '/api/mobile/iq-test/start', { method: 'POST', body: JSON.stringify({ instrument: this.instrument, testlauf: this.testlauf }) });
        this.run = { session, startedAt: Date.now(), phase: 'intro', idx: 0, answers: {}, uebung: 0 };
        this.save();
        this.vorbereiten();
        this.showIntro();
      } catch (e) {
        this.fehler(e.message || 'Der Test konnte nicht starten.', () => this.start());
      }
    }
    vorbereiten() {
      this.items = this.run.session.blocks.flatMap((b) => b.items);
      this.block = this.run.session.blocks[0];
      // Bilder vorladen (Reaktionszeiten sollen nicht vom Netz abhängen)
      for (const it of [...(this.run.session.practice || []).map((p) => p.item), ...this.items]) if (it.imageUrl) { const im = new Image(); im.src = it.imageUrl; }
    }
    resume(run) {
      this.run = run;
      this.vorbereiten();
      if (run.phase === 'angaben') this.showAngaben();
      else if (run.phase === 'test') this.showItem();
      else this.showIntro();
    }

    // ── Anleitung + Übung ────────────────────────────────────────────────
    showIntro() {
      this.clear();
      const practice = this.run.session.practice || [];
      const tasten = this.items[0] && this.items[0].type === 'bildpaar';
      const wrap = h('div', { class: 'thct-blockstart' },
        h('h2', { class: 'thct-titel', tabindex: '-1', text: this.block.title }),
        h('p', { class: 'thct-text', text: this.block.instruction }),
        tasten ? h('p', { class: 'thct-klein' }, 'Am Computer geht es mit der Tastatur am schnellsten: ', h('b', { text: 'F' }), ' oder ', h('b', { text: '←' }), ' für „gleich“, ', h('b', { text: 'J' }), ' oder ', h('b', { text: '→' }), ' für „verschieden“.') : null,
        h('div', { class: 'thct-aktionen' },
          practice.length ? h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: () => this.showUebung(0) }, practice.length + ' Übungsaufgaben') : h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: () => this.startTest() }, 'Los geht’s')));
      this.stage.replaceChildren(wrap);
      scrollTop(this);
      wrap.querySelector('.thct-titel').focus({ preventScroll: true });
    }

    showUebung(i) {
      this.clear();
      this.guardOn();
      const practice = this.run.session.practice;
      if (i >= practice.length) {
        const wrap = h('div', { class: 'thct-blockstart' },
          h('h2', { class: 'thct-titel', tabindex: '-1', text: 'Übung geschafft' }),
          h('p', { class: 'thct-text', text: 'Jetzt kommen ' + this.items.length + ' Aufgaben ohne Rückmeldung. Antworte so schnell du kannst, aber ohne zu raten. Für jedes Bild hast du 7,5 Sekunden.' }),
          h('div', { class: 'thct-aktionen' }, h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: () => this.startTest() }, 'Test starten')));
        this.stage.replaceChildren(wrap);
        wrap.querySelector('.thct-titel').focus({ preventScroll: true });
        return;
      }
      const p = practice[i];
      this.renderBildpaar(p.item, { kopf: 'Übung ' + (i + 1) + ' von ' + practice.length, feedback: true }, (antwort) => {
        const ok = antwort === p.solution;
        const box = h('div', { class: 'thco-feedback ' + (ok ? 'is-ok' : 'is-falsch'), role: 'status' },
          h('p', null, h('b', { text: antwort === null ? 'Zeit abgelaufen. ' : ok ? 'Richtig. ' : 'Nicht richtig. ' }), p.explanation),
          h('button', { type: 'button', class: 'th-btn th-btn--secondary', onclick: () => this.showUebung(i + 1) }, i + 1 < practice.length ? 'Nächste Übung' : 'Weiter'));
        this.stage.querySelector('.thco-aufgabe').append(box);
        box.querySelector('button').focus({ preventScroll: true });
      });
    }

    startTest() {
      this.run.phase = 'test';
      this.run.idx = this.run.idx || 0;
      this.save();
      this.showItem();
    }

    // ── Aufgaben ──────────────────────────────────────────────────────────
    showItem() {
      this.clear();
      if (this.run.idx >= this.items.length) return this.showAngaben();
      this.guardOn();
      const it = this.items[this.run.idx];
      const kopf = 'Aufgabe ' + (this.run.idx + 1) + ' von ' + this.items.length;
      if (it.type === 'bildpaar') {
        this.renderBildpaar(it, { kopf, feedback: false }, (antwort, rt, timeout) => {
          this.run.answers[it.id] = { id: it.id, answer: antwort, rtMs: rt, timedOut: !!timeout };
          this.run.idx++;
          this.save();
          // kurze Leerphase wie im Original (250 ms)
          this.stage.querySelector('.thco-bild').classList.add('is-leer');
          this._t = setTimeout(() => this.showItem(), 250);
        });
      } else {
        this.renderFreitext(it, kopf);
      }
    }

    fortschritt(kopf) {
      const pct = Math.round((this.run.idx / this.items.length) * 100);
      return h('div', { class: 'thct-kopf' },
        h('div', { class: 'thct-kopf__zeile' }, h('span', { class: 'thct-kopf__block' }, h('b', { text: this.block.title })), h('span', { class: 'thct-kopf__nr', text: kopf })),
        h('div', { class: 'thct-fortschritt', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': String(this.items.length), 'aria-valuenow': String(this.run.idx), 'aria-label': 'Fortschritt' }, h('span', { style: 'width:' + pct + '%' })));
    }

    renderBildpaar(it, opts, fertig) {
      let start = 0;
      let laeuft = false;
      let geplant = false;
      let erledigt = false;
      const limit = it.zeitlimitMs || 7500;
      const zeitbalken = h('span', { class: 'thco-zeit__balken' });
      const done = (antwort, timeout) => {
        if (erledigt || !laeuft) return;
        erledigt = true;
        this.clear();
        const rt = Math.round(performance.now() - start);
        this.stage.querySelectorAll('.thco-knopf').forEach((b) => { b.disabled = true; if (b.dataset.id === antwort) b.classList.add('is-gewaehlt'); });
        fertig(antwort, rt, timeout);
      };
      const knopf = (o, taste) => h('button', { type: 'button', class: 'thco-knopf', 'data-id': o.id, onclick: () => done(o.id, false) }, h('span', { class: 'thco-knopf__text', text: o.text }), h('span', { class: 'thco-knopf__taste', 'aria-hidden': 'true', text: taste }));
      const frage = h('h2', { class: 'thct-frage', tabindex: '-1', text: it.prompt });
      const body = h('div', { class: 'thco-aufgabe' },
        frage,
        h('figure', { class: 'thco-bild' }, h('img', { alt: it.imageAlt || '', width: '800', height: '427', decoding: 'sync' })),
        h('div', { class: 'thco-zeit', 'aria-hidden': 'true' }, zeitbalken),
        h('div', { class: 'thco-knoepfe' }, knopf(it.options[0], 'F'), knopf(it.options[1], 'J')));
      this.stage.replaceChildren(opts.feedback ? h('p', { class: 'thct-eyebrow', text: opts.kopf }) : this.fortschritt(opts.kopf), body);
      if (this.run.idx === 0 || opts.feedback) scrollTop(this);
      frage.focus({ preventScroll: true });
      // Uhr erst starten, wenn das Bild wirklich zu sehen ist (Ladezeit zählt nicht zur Reaktionszeit)
      const img = body.querySelector('img');
      const los = () => {
        if (geplant || erledigt || !img.isConnected) return;
        geplant = true;
        // Uhr per setTimeout starten (requestAnimationFrame ruht in Hintergrund-Tabs), Balken-Animation per rAF
        setTimeout(() => {
          if (!img.isConnected || erledigt) return;
          laeuft = true;
          start = performance.now();
          zeitbalken.style.transitionDuration = limit + 'ms';
          requestAnimationFrame(() => zeitbalken.classList.add('is-laeuft'));
          this._t = setTimeout(() => done(null, true), limit);
        }, 0);
      };
      img.addEventListener('load', los, { once: true });
      img.addEventListener('error', los, { once: true });
      img.src = it.imageUrl;
      if (img.complete && img.naturalWidth) los();
      document.onkeydown = (ev) => {
        if (ev.altKey || ev.ctrlKey || ev.metaKey || this.querySelector('.thct-verlassen')) return;
        const k = ev.key.toLowerCase();
        if (k === 'f' || ev.key === 'ArrowLeft') { ev.preventDefault(); done(it.options[0].id, false); }
        else if (k === 'j' || ev.key === 'ArrowRight') { ev.preventDefault(); done(it.options[1].id, false); }
      };
    }

    renderFreitext(it, kopf) {
      const start = performance.now();
      const prev = this.run.answers[it.id];
      const inp = h('input', { class: 'thct-input thco-text', type: 'text', maxlength: String(it.format.maxLength || 60), autocomplete: 'off', autocapitalize: 'off', spellcheck: 'false', 'aria-label': 'Deine Antwort', value: prev ? prev.answer || '' : '' });
      const kannte = h('input', { type: 'checkbox', id: 'thco-kannte', checked: !!(prev && prev.kannte) });
      const weiter = h('button', { type: 'button', class: 'th-btn th-btn--primary', disabled: !(prev && prev.answer), onclick: () => { if (inp.value.trim()) speichern(false); } }, this.run.idx === this.items.length - 1 ? 'Abschließen' : 'Weiter');
      const speichern = (leer) => {
        this.run.answers[it.id] = { id: it.id, answer: leer ? null : inp.value.trim() || null, rtMs: Math.round(performance.now() - start) + (prev ? prev.rtMs || 0 : 0), kannte: kannte.checked };
        this.run.idx++;
        this.save();
        this.showItem();
      };
      inp.addEventListener('input', () => { weiter.disabled = inp.value.trim() === ''; });
      // Kein <form> (Googles Formularerkennung, QA-Runde 3, Opus N-10); Enter im Feld schickt ab wie vorher
      inp.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); if (inp.value.trim()) speichern(false); } });
      const form = h('div', { class: 'thco-aufgabe' },
        h('h2', { class: 'thct-frage thco-fragetext', tabindex: '-1', text: it.prompt }),
        h('div', { class: 'thct-feld' }, h('label', { class: 'thct-eingabe__label', for: 'thco-antwort', text: 'Deine Antwort' }), inp),
        it.kennenAbfragen ? h('label', { class: 'thct-check', for: 'thco-kannte' }, kannte, ' Diese Frage kannte ich schon') : null,
        h('div', { class: 'thct-aktionen thct-aktionen--item' }, h('button', { type: 'button', class: 'thct-link', onclick: () => speichern(true) }, 'Weiß ich nicht'), weiter));
      inp.id = 'thco-antwort';
      this.stage.replaceChildren(this.fortschritt(kopf), form);
      scrollTop(this);
      setTimeout(() => inp.focus({ preventScroll: true }), 50);
    }

    // ── Angaben (kurz) ────────────────────────────────────────────────────
    showAngaben() {
      this.clear();
      this.guardOff(); // keine Aufgabe mehr offen, Verlassen ist hier unkritisch
      this.run.phase = 'angaben';
      this.save();
      const a = (this.run.angaben = this.run.angaben || {});
      const chips = (name, label, opts) => {
        const grp = h('div', { class: 'thct-chips', role: 'radiogroup', 'aria-label': label });
        opts.forEach(([v, t]) => {
          const b = h('button', { type: 'button', class: 'thct-chip', role: 'radio', 'aria-checked': a[name] === v ? 'true' : 'false', onclick: () => {
            a[name] = a[name] === v ? null : v;
            grp.querySelectorAll('.thct-chip').forEach((x) => x.setAttribute('aria-checked', x.dataset.v === a[name] ? 'true' : 'false'));
            this.save();
          } }, t);
          b.dataset.v = v;
          grp.append(b);
        });
        return h('fieldset', { class: 'thct-feld' }, h('legend', { text: label }), grp);
      };
      const alter = h('input', { class: 'thct-input thct-input--kurz', id: 'thct-alter', type: 'text', inputmode: 'numeric', pattern: '[0-9]*', maxlength: '2', autocomplete: 'off', value: a.alter || '', oninput: (ev) => { ev.target.value = ev.target.value.replace(/[^0-9]/g, ''); a.alter = ev.target.value || null; this.save(); } });
      const optOut = h('input', { type: 'checkbox', id: 'thct-optout', checked: !!a.statistikOptOut, onchange: (ev) => { a.statistikOptOut = ev.target.checked; this.save(); } });
      // Kein <form> (Googles Formularerkennung, QA-Runde 3, Opus N-10); Enter im Altersfeld schickt ab wie vorher
      alter.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); this.submit(false); } });
      const form = h('div', { class: 'thct-angaben', role: 'group', 'aria-labelledby': 'thct-angaben-t' },
        h('p', { class: 'thct-eyebrow', text: 'Fast geschafft' }),
        h('h2', { class: 'thct-titel', id: 'thct-angaben-t', tabindex: '-1', text: 'Zwei, drei freiwillige Angaben' }),
        h('p', { class: 'thct-text', text: 'Damit können wir die Ergebnisse besser einordnen. Dein Ergebnis siehst du in jedem Fall.' }),
        h('div', { class: 'thct-angaben__raster' },
          h('div', { class: 'thct-feld' }, h('label', { for: 'thct-alter', text: 'Alter in Jahren' }), alter),
          chips('geschlecht', 'Geschlecht', [['w', 'weiblich'], ['m', 'männlich'], ['d', 'divers'], ['k', 'keine Angabe']]),
          chips('vorerfahrung', 'Schon einmal gemacht?', [['nie', 'Noch nie'], ['andere', 'Ähnliche Aufgaben'], ['dieser', 'Genau diesen Test']])),
        h('label', { class: 'thct-check thct-check--optout', for: 'thct-optout' }, optOut, ' Meinen Durchlauf nicht in die Statistik aufnehmen'),
        // vollständig wie im Artikel (QA final, Fable S10)
        h('p', { class: 'thct-hilfe' }, 'Gespeichert werden Antworten, Zeiten, Ergebnis, Geräteart und diese Angaben, das Alter nur als Altersgruppe, mit Tagesdatum und ohne Namen, E-Mail oder IP-Adresse' + (this.items.some((x) => x.type === 'freitext') ? '; von deinen Freitext-Antworten nur, ob sie richtig waren' : '') + '. ', h('a', { href: '/policies/privacy-policy', text: 'Datenschutz' }), '.'),
        h('div', { class: 'thct-aktionen' },
          h('button', { type: 'button', class: 'thct-link', onclick: () => this.submit(true) }, 'Ohne Angaben zum Ergebnis'),
          h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: () => this.submit(false) }, 'Ergebnis anzeigen')));
      this.stage.replaceChildren(form);
      scrollTop(this);
      form.querySelector('.thct-titel').focus({ preventScroll: true });
    }

    async submit(ohne) {
      const a = ohne ? { statistikOptOut: !!(this.run.angaben || {}).statistikOptOut } : Object.assign({}, this.run.angaben || {});
      const hist = store.get(this.kHist) || { anzahl: 0 };
      const answers = this.items.map((it) => this.run.answers[it.id] || { id: it.id, answer: null, rtMs: 0, timedOut: true });
      this.stageHalten(2);
      this.msg('Dein Ergebnis wird berechnet …', true);
      try {
        const res = await api(this.api, '/api/mobile/iq-test/submit', { method: 'POST', body: JSON.stringify({
          token: this.run.session.token,
          answers,
          angaben: { alter: a.alter ? Number(a.alter) : null, geschlecht: a.geschlecht, vorerfahrung: a.vorerfahrung, statistikOptOut: !!a.statistikOptOut },
          geraet: geraet(),
          fruehereDurchlaeufe: hist.anzahl || 0,
          seite: location.pathname,
        }) });
        store.del(this.kRun);
        store.set(this.kHist, { anzahl: (hist.anzahl || 0) + 1, letztes: { raw: res.raw, max: res.max, am: Date.now() } });
        this.showResult(res);
      } catch (e) {
        if (e.status === 400) {
          // Sitzung abgelaufen, ungültig oder alte Fassung: kein „Nochmal versuchen“ in die Sackgasse (Fable D3)
          this.fehler((e.message || 'Die Testsitzung ist abgelaufen oder ungültig.') + ' Diese Antworten lassen sich leider nicht mehr auswerten.', null, () => this.neuStart());
          return;
        }
        // Freitext liegt nur in dieser Seite, nicht im Speicher (QA final, Fable S9)
        const frei = this.items.some((x) => x.type === 'freitext');
        this.fehler((e.message || 'Die Auswertung ist fehlgeschlagen.') + (frei ? ' Deine Antworten sind noch in dieser Seite; bitte lade sie nicht neu.' : ' Deine Antworten sind auf diesem Gerät gespeichert.'), () => this.submit(ohne));
      }
    }

    // ── Ergebnis ──────────────────────────────────────────────────────────
    showResult(r) {
      this.clear();
      this.guardOff();
      const a = r.auswertung || {};
      const wrap = h('div', { class: 'thct-ergebnis' }, h('p', { class: 'thct-eyebrow', text: 'Dein Ergebnis' }));
      if (r.auswertbar === false) {
        // Mentale Rotation mit weniger als der Hälfte beantworteter Bilder (Abnahme 2, Opus N2): keine Einordnung,
        // keine Produkte, kein Teilen, nur der eine Neustart-Knopf, wie beim IQ-Test
        const beantwortet = typeof a.beantwortet === 'number' ? a.beantwortet : null;
        wrap.append(
          h('h2', { class: 'thct-titel', tabindex: '-1', text: 'Dieser Durchlauf ist nicht auswertbar' }),
          h('div', { class: 'thct-hinweis thct-hinweis--gross', role: 'note' },
            h('p', { text: 'Wir zeigen dir bewusst keine Einordnung, weil sie bei diesem Durchlauf nichts aussagen würde:' }),
            h('ul', null, ...(r.nichtAuswertbarGrund || []).map((t) => h('li', { text: t }))),
            h('p', { text: (beantwortet !== null ? beantwortet + ' von ' + r.max + ' Bildern beantwortet, ' : '') + r.raw + ' richtig. Für einen neuen Versuch: Jedes Bild bleibt nur kurz stehen, entscheide dich lieber zügig, auch wenn du unsicher bist.' })),
          h('div', { class: 'thct-aktionen' }, h('button', { type: 'button', class: 'th-btn th-btn--primary', onclick: () => this.neuStart() }, 'In Ruhe neu starten')));
        if (r.attribution) wrap.append(h('p', { class: 'thct-klein thco-quelle', text: r.attribution }));
        this.stage.replaceChildren(wrap);
        scrollTop(this);
        wrap.querySelector('.thct-titel').focus({ preventScroll: true });
        return;
      }
      if (a.art === 'mentale-rotation') this.ergebnisRotation(wrap, r, a);
      else if (a.art === 'denkfallen') this.ergebnisDenkfallen(wrap, r, a);
      else wrap.append(h('h2', { class: 'thct-titel', tabindex: '-1', text: r.raw + ' von ' + r.max + ' richtig' }));
      if (r.hinweise && r.hinweise.length) wrap.append(h('div', { class: 'thct-hinweis', role: 'note' }, ...r.hinweise.map((t) => h('p', { text: t }))));
      const c = r.community;
      if (c) wrap.append(h('section', { class: 'thct-vergleich' }, h('h3', { text: 'Im Vergleich mit anderen Teilnehmenden' }),
        c.besserAlsProzent !== null ? h('p', null, 'Du hast mehr Aufgaben gelöst als ', h('b', { text: c.besserAlsProzent + ' %' }), ' der bisher ' + NF0.format(c.n) + ' gewerteten Teilnahmen.') : h('p', { text: 'Der Vergleich erscheint ab 30 gewerteten Teilnahmen. Bisher sind es ' + NF0.format(c.n) + '.' }),
        h('p', { class: 'thct-klein', text: 'Die Teilnehmenden haben sich selbst für den Test entschieden, die Gruppe ist nicht repräsentativ. ' + (r.inStatistik ? 'Dein Durchlauf fließt ohne Namen in die Statistik ein.' : 'Dein Durchlauf fließt nicht in die Statistik ein.') })));
      // im Ratebereich keine Produktkarten (QA-Runde 3, Opus N-8b), wie beim IQ-Test ohne Auswertung
      if (!(a.art === 'mentale-rotation' && a.imRatebereich)) wrap.append(this.empfehlungen());
      wrap.append(this.teilen(r));
      if (r.attribution) wrap.append(h('p', { class: 'thct-klein thco-quelle', text: r.attribution }));
      this.stage.replaceChildren(wrap);
      scrollTop(this);
      const t = wrap.querySelector('.thct-titel');
      if (t) t.focus({ preventScroll: true });
    }

    kachel(einheit, gross, unter) {
      return h('div', { class: 'thct-held__zahl' }, h('span', { class: 'thct-held__einheit', text: einheit }), h('span', { class: 'thct-held__iq', text: gross }), h('span', { class: 'thct-held__band', text: unter }));
    }

    ergebnisRotation(wrap, r, a) {
      const prozent = Math.round(a.anteil * 100);
      // Rateniveau zuerst: unter 26 von 40 nicht vom Raten zu unterscheiden (Opus MR-2)
      const band = a.imRatebereich ? 'im Bereich des Ratens' : a.einordnung === 'ueber' ? 'über dem Durchschnitt der Pilotgruppe' : a.einordnung === 'unter' ? 'unter dem Durchschnitt der Pilotgruppe' : 'im Bereich der Pilotgruppe';
      wrap.append(
        h('h2', { class: 'thct-titel', tabindex: '-1', text: 'Mentale Rotation: ' + r.raw + ' von ' + r.max + ' richtig' }),
        h('div', { class: 'thct-held' },
          // mit Zeitüberschreitungen auch die Quote je beantworteter Aufgabe, passend zur Rategrenze (QA final, Fable S11)
          this.kachel('Trefferquote', prozent + ' %', r.raw + ' von ' + r.max + ' richtig' + (a.zeitUeberschritten && a.beantwortet ? ', ' + r.raw + ' von ' + a.beantwortet + ' beantworteten' : '')),
          h('div', { class: 'thct-held__text' },
            h('p', null, 'Einordnung: ', h('b', { text: band }), '. Die 54 jungen Erwachsenen der Originalstudie (Harvard, im Schnitt 22 Jahre) lösten im Schnitt ' + NF1.format(a.referenz.mittel * 100) + ' % (Standardabweichung ' + NF1.format(a.referenz.sd * 100) + ' Prozentpunkte, umgerechnet auf ' + r.max + ' Aufgaben), im Labor am Computer.'),
            // Rateniveau nur über die beantworteten Aufgaben (QA-Runde 3, Opus N-8a); ältere Antworten ohne „beantwortet“
            a.imRatebereich ? h('p', { text: 'Unter ' + (a.rateGrenze || 26) + ' von ' + (a.beantwortet || r.max) + ' beantworteten Aufgaben richtig lässt sich ein Ergebnis nicht vom Raten unterscheiden: Mit reinem Raten wären etwa ' + Math.round((a.beantwortet || r.max) / 2) + ' richtig.' }) : null,
            a.steigungMsJeGrad === null || a.steigungMsJeGrad === undefined ? null : a.steigungMsJeGrad > 0.5
              ? h('p', null, 'Deine Drehgeschwindigkeit: Pro Grad Drehung brauchtest du im Mittel ', h('b', { text: NF1.format(a.steigungMsJeGrad) + ' ms' }), ' länger (Originalstudie: ' + NF1.format(a.referenzSteigungMsJeGrad) + ' ms). Je stärker eine Figur gedreht ist, desto länger dauert das Drehen im Kopf.')
              : h('p', { text: 'Deine Zeiten hingen kaum vom Drehwinkel ab (' + NF1.format(a.steigungMsJeGrad) + ' ms je Grad, Originalstudie: ' + NF1.format(a.referenzSteigungMsJeGrad) + ' ms). Meist heißt das, dass ohne Drehen im Kopf entschieden oder geraten wurde.' }),
            a.zeitUeberschritten ? h('p', { class: 'thct-klein', text: a.zeitUeberschritten + '-mal war die Zeit abgelaufen; das zählt als nicht gelöst.' }) : null,
            h('p', { class: 'thct-klein', text: 'Kein IQ-Test: Gemessen wird ein Teil des räumlichen Vorstellens. Bildschirmgröße, Gerät und Bedienung (Tippen statt Tastatur) beeinflussen die Zeiten.' }))),
        this.winkelChart(a));
    }

    winkelChart(a) {
      // je Winkel: richtige von den beantworteten, Zeitüberschreitungen getrennt (QA final, Fable S11); ältere Antworten ohne „beantwortet“
      const treffer = (p) => {
        if (!p.n) return '–';
        const b = p.beantwortet !== undefined ? p.beantwortet : p.n;
        const abgelaufen = p.zeitUeberschritten || 0;
        if (!b) return 'keine beantwortet (' + abgelaufen + '-mal Zeit abgelaufen)';
        return Math.round((p.richtig / b) * 100) + '\u00a0% (' + p.richtig + ' von ' + b + (p.beantwortet !== undefined ? ' beantworteten' : '') + ')' + (abgelaufen ? ', ' + abgelaufen + '-mal Zeit abgelaufen' : '');
      };
      const W = Math.round(Math.min(760, Math.max(300, this.stage.clientWidth - 8))), H = 230, L = 46, R = 16, T = 16, B = 180;
      const pts = a.jeWinkel;
      // Vergleich wie im Paper: Mittel der richtigen Antworten, auf ±2 SD getrimmt (ältere Ergebnisse: Median)
      const rt = (p) => (p.mittelRtMs !== undefined ? p.mittelRtMs : p.medianRtMs);
      const maxRt = Math.max(3600, ...pts.map((p) => rt(p) || 0));
      const x = (w) => L + (w / 150) * (W - L - R);
      const y = (ms) => B - (ms / maxRt) * (B - T);
      const svg = s('svg', { viewBox: '0 0 ' + W + ' ' + H, class: 'thco-chart', role: 'img', 'aria-label': 'Reaktionszeit je Drehwinkel: deine Werte im Vergleich zur Originalstudie' });
      for (const v of [0, 1000, 2000, 3000]) if (v <= maxRt) svg.append(s('line', { x1: L, x2: W - R, y1: y(v), y2: y(v), class: 'thct-gitter' }), s('text', { x: L - 6, y: y(v) + 4, 'text-anchor': 'end', class: 'thct-achsentext' }, NF1.format(v / 1000) + ' s'));
      pts.forEach((p) => svg.append(s('text', { x: x(p.winkel), y: B + 18, 'text-anchor': 'middle', class: 'thct-achsentext' }, p.winkel + '°')));
      const linie = (vals, farbe) => {
        const d = vals.filter((v) => v[1] != null).map((v, i) => (i ? 'L' : 'M') + x(v[0]).toFixed(1) + ',' + y(v[1]).toFixed(1)).join('');
        if (d) svg.append(s('path', { d, fill: 'none', stroke: farbe, 'stroke-width': 2, 'stroke-linejoin': 'round' }));
        vals.forEach((v) => { if (v[1] != null) svg.append(s('circle', { cx: x(v[0]), cy: y(v[1]), r: 5, fill: farbe, stroke: '#fff', 'stroke-width': 2 })); });
      };
      linie(pts.map((p) => [p.winkel, p.referenzRtMs]), '#5E6E80');
      linie(pts.map((p) => [p.winkel, rt(p)]), '#3A6AAE');
      const tabelle = h('table', null, h('thead', null, h('tr', null, ...['Drehung', 'Deine Treffer', 'Studie', 'Deine Zeit', 'Studie'].map((t) => h('th', { scope: 'col', text: t })))),
        h('tbody', null, ...pts.map((p) => h('tr', null, h('th', { scope: 'row', text: p.winkel + '°' }), h('td', { text: treffer(p) }), h('td', { text: Math.round(p.referenzAnteil * 100) + ' %' }), h('td', { text: rt(p) ? NF1.format(rt(p) / 1000) + ' s' : '–' }), h('td', { text: NF1.format(p.referenzRtMs / 1000) + ' s' })))));
      return h('figure', { class: 'thct-fig thco-fig' },
        h('ul', { class: 'thct-legende' }, h('li', null, h('i', { class: 'is-linie', style: 'background:#3A6AAE' }), 'Du (Mittel der richtigen Antworten, Ausreißer über 2 Standardabweichungen ausgenommen)'), h('li', null, h('i', { class: 'is-linie', style: 'background:#5E6E80' }), 'Originalstudie (gleich berechnet, N = 54)')),
        svg,
        h('figcaption', { class: 'thct-klein', text: 'Je stärker die rechte Figur gedreht ist, desto länger dauert die Entscheidung. Das ist der klassische Befund von Shepard und Metzler (1971).' }),
        h('div', { class: 'thct-tabelle' }, tabelle));
    }

    ergebnisDenkfallen(wrap, r, a) {
      const text = r.raw === 4 ? 'Du hast alle vier Fallen erkannt.' : r.raw >= 2 ? 'Das ist der typische Bereich.' : 'Deine Intuition war schneller als dein Nachprüfen. Das ist sehr häufig und kein Zeichen für geringe Intelligenz.';
      wrap.append(
        h('h2', { class: 'thct-titel', tabindex: '-1', text: 'Denkfallen: ' + r.raw + ' von 4 richtig' }),
        h('div', { class: 'thct-held' },
          this.kachel('Richtige Antworten', r.raw + ' / 4', a.intuitiv ? a.intuitiv + '-mal in die Falle' : 'nie in die Falle'),
          h('div', { class: 'thct-held__text' },
            h('p', null, h('b', { text: text }), ' Im Original (143 Studierende) waren es im Schnitt ' + a.referenz.mittlerePunktzahl.toLocaleString('de-DE', { maximumFractionDigits: 2 }) + ' von 4.'),
            a.ohneBekannte && a.ohneBekannte.n < 4 ? h('p', { text: 'Ohne die Fragen, die du schon kanntest: ' + a.ohneBekannte.richtig + ' von ' + a.ohneBekannte.n + ' richtig.' }) : null,
            // ehrlicher Vergleich (Opus CRT-2): im Original standen alle Fragen auf einer Seite
            h('p', { class: 'thct-klein', text: 'Zum Vergleich: Im Original standen alle vier Fragen auf einer Seite, Antworten ließen sich bis zum Schluss ändern, und manche kannten die Fragen schon. Die Autoren nennen das selbst als Grund für die eher hohen Werte. Hier kommt eine Frage nach der anderen, der Vergleich ist also eher streng.' }),
            h('p', { class: 'thct-klein', text: 'Kein IQ-Test: Gemessen wird eine Denkgewohnheit, nämlich ob du die erste, naheliegende Antwort noch einmal prüfst. Mit vier Fragen ist das Ergebnis nur eine grobe Momentaufnahme (Reliabilität im Original α = 0,51).' }))));
      const list = h('ol', { class: 'thct-loesungen__liste' });
      for (const x of r.review || []) {
        const nr = Number(String(x.id).replace('F', ''));
        const f = (a.jeFrage || []).find((q) => q.nr === nr) || {};
        const it = this.items.find((i) => i.id === x.id);
        const status = x.antwort === 'richtig' ? '✓ Richtig' : x.antwort === 'intuitiv' ? '✗ In die Falle getappt' : x.antwort === 'sonstig' ? '✗ Andere Antwort' : '○ Keine Antwort';
        list.append(h('li', { class: 'thct-loesung ' + (x.richtig ? 'is-richtig' : 'is-falsch') },
          h('p', { class: 'thct-loesung__kopf' }, h('b', { text: status }), x.kannte ? ' (kanntest du schon)' : ''),
          h('p', { text: it ? it.prompt : '' }),
          h('p', null, 'Lösung: ', h('b', { text: x.loesung || '' }), x.falle ? ' · Typische Falle: ' + x.falle : ''),
          x.erklaerung ? h('p', { class: 'thct-loesung__erkl', text: x.erklaerung }) : null,
          f.originalRichtig ? h('p', { class: 'thct-klein', text: 'In der Originalstudie richtig: ' + Math.round(f.originalRichtig * 100) + ' %' }) : null));
      }
      wrap.append(h('section', { class: 'thct-profil' }, h('h3', { text: 'Die vier Fragen mit Lösung' }), list));
    }

    empfehlungen() {
      const p = this.produkte.slice(0, 2);
      if (!p.length) return h('div');
      return h('section', { class: 'thct-empfehlung', 'aria-labelledby': 'thco-empf-t' },
        h('h3', { id: 'thco-empf-t', text: 'Gezielt weiter üben' }),
        h('p', { class: 'thct-klein', text: 'Wenn du dich auf einen Einstellungs- oder Eignungstest vorbereitest, passen diese Trainer:' }),
        h('div', { class: 'thct-empfehlung__raster' }, ...p.map((x) => h('a', { class: 'thct-produkt', href: x.url },
          x.bild ? h('img', { src: x.bild, alt: '', width: '96', height: '96', loading: 'lazy' }) : null,
          h('span', { class: 'thct-produkt__text' }, h('b', { text: x.titel }), SATZ[x.handle] ? h('span', { class: 'thct-klein', text: SATZ[x.handle] }) : null, h('span', { class: 'thct-produkt__preis', text: x.preis ? 'ab ' + x.preis + ' · kein Abo' : '' }))))));
    }

    teilen(r) {
      const url = location.origin + location.pathname;
      const text = 'Mein Ergebnis im ' + (this.instrument === 'denkfallen' ? 'Denkfallen-Test' : 'Mentale-Rotation-Test') + ' von TestHelden: ' + r.raw + ' von ' + r.max + '. Probier ihn selbst:';
      const status = h('span', { class: 'thct-klein', 'aria-live': 'polite' });
      return h('div', { class: 'thct-aktionen thct-aktionen--teilen' },
        h('button', { type: 'button', class: 'th-btn th-btn--secondary', onclick: async () => { try { if (navigator.share) await navigator.share({ title: document.title, text, url }); else { await navigator.clipboard.writeText(text + ' ' + url); status.textContent = 'Link kopiert.'; } } catch (e) { /* abgebrochen */ } } }, 'Ergebnis teilen'),
        status,
        h('a', { class: 'thct-link', href: '/blogs/magazin/iq-test', text: 'Zum IQ-Test' }),
        h('button', { type: 'button', class: 'thct-link', onclick: () => this.neuStart() }, 'Nochmal machen'));
    }
  }

  customElements.define('th-cogtest-offen', ThCogtestOffen);
})();
